import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CallStateEnum } from '@models/call-state.model.ts';
import {
  signInRefreshToken,
  signIn,
  verifyBeforePasswordChange,
  changePassword,
  getUsersToken,
  getUsersBranding
} from '@api/auth.api.ts';
import useTokenStore from '@store/token-store/token.store.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import { RoutePaths } from '@utils/route.utils.ts';
import { AUTH_INITIAL_STATE, AuthState } from '@store/auth-store/auth.model.ts';
import axios from 'axios';
import { UserRole } from '@utils/roles-utils.ts';
import useOrganizationBrandingStore from '@store/organization-branding-store/organization-branding.store.ts';
import { logoutOneSignal } from '@services/onesignal/onesignal.service.ts';
import { getRootDomainFromHost } from '@utils/hostname-utils.ts';

const { showSnackbar } = useSnackbarStore.getState();
export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const useAuthStore = create<AuthState>()(
  devtools((set, getState) => ({
    ...AUTH_INITIAL_STATE,
    ...withCallState(getState),

    getUserBranding: async (domain: string) => {
      const res = await getUsersBranding(domain);

      if (res.primaryColor)
        useOrganizationBrandingStore
          .getState()
          .setPrimaryColor(res.primaryColor);
      if (res.logo) useOrganizationBrandingStore.getState().setImage(res.logo);
    },

    switchHotel: async (hotelId: number | null) => {
      set({
        switchHotelCallState: CallStateEnum.Loading
      });

      try {
        const { superAdminAccessToken, refreshToken } =
          useTokenStore.getState();
        const { setPrimaryColor, setImage } =
          useOrganizationBrandingStore.getState();

        if (hotelId === null) {
          if (!superAdminAccessToken) {
            throw new Error('No Super Admin token found');
          }

          useTokenStore
            .getState()
            .setTokens(superAdminAccessToken, refreshToken);
          set({ selectedHotelId: null });

          const rootDomain = getRootDomainFromHost();
          try {
            const res: any = await getUsersBranding(rootDomain);

            if (res.primaryColor) setPrimaryColor(res.primaryColor);
            if (res.logo) setImage(res.logo);
          } catch (brandingError) {
            void brandingError;
          }
        } else {
          const tokenData = await getUsersToken(hotelId);
          useTokenStore
            .getState()
            .setTokens(tokenData.accessToken, tokenData.refreshToken);
          set({ selectedHotelId: hotelId });
        }

        getState().stopTokenRefreshInterval();
        getState().startTokenRefreshInterval();

        await delay(1000);

        if (hotelId === null) {
          window.location.href = RoutePaths.Core.HotelManagement.absolutePath;
        } else {
          window.location.href = RoutePaths.Core.Analytics.absolutePath;
        }

        set({ switchHotelCallState: CallStateEnum.Loaded });
      } catch (error) {
        set({ switchHotelCallState: CallStateEnum.Error });

        if (axios.isAxiosError(error)) {
          const message = error.response?.data?.message;
          showSnackbar(message, SnackbarType.Error);
        }
      }
    },

    signIn: async ({ email, password }, navigate) => {
      set({ callState: CallStateEnum.Loading });
      try {
        const data = await signIn({ email, password });

        useTokenStore.getState().setTokens(data.accessToken, data.refreshToken);

        if (
          data.role === UserRole.SUPER_ADMIN ||
          data.role === UserRole.PARTNER
        ) {
          useTokenStore.getState().setSuperAdminToken(data.accessToken);
          navigate(RoutePaths.Core.HotelManagement.absolutePath);
        } else {
          navigate(RoutePaths.Core.Analytics.absolutePath);
        }

        set({
          callState: CallStateEnum.Loaded
        });

        getState().startTokenRefreshInterval();
      } catch (error: any) {
        if (error) {
          set({ signInCallStateError: 'sign_in_error' });
        }
        set({ callState: CallStateEnum.Error });
      }
    },

    logout: () => {
      void logoutOneSignal();
      useTokenStore.getState().clearTokens();
      set({
        callState: CallStateEnum.Loaded
      });
      getState().stopTokenRefreshInterval();
      window.location.href = RoutePaths.Auth.SignIn.absolutePath;
    },

    refreshToken: async () => {
      const refreshToken = useTokenStore.getState().refreshToken;
      if (!refreshToken) return;

      try {
        const data = await signInRefreshToken(refreshToken);

        useTokenStore.getState().setTokens(data.accessToken, data.refreshToken);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message = error.response?.data?.message;
          showSnackbar(message, SnackbarType.Error);
        }
        set({ callState: CallStateEnum.Error });
      }
    },

    verifyBeforePasswordChange: async ({ password }, navigate) => {
      set({ callState: CallStateEnum.Loading });
      try {
        await verifyBeforePasswordChange({ password });

        set({
          callState: CallStateEnum.Loaded
        });

        navigate(RoutePaths.ChangePassword.absolutePath);
      } catch (error) {
        set({ callState: CallStateEnum.Error });
        if (axios.isAxiosError(error)) {
          const message = error.response?.data?.message;
          showSnackbar(message, SnackbarType.Error);
        }
      }
    },

    changePassword: async ({ password }) => {
      set({ callState: CallStateEnum.Loading });
      try {
        await changePassword({ password });

        set({
          callState: CallStateEnum.Loaded
        });
        showSnackbar('change_password_success_text', SnackbarType.Success);
        getState().logout();
      } catch (error) {
        set({ callState: CallStateEnum.Error });
        if (axios.isAxiosError(error)) {
          const message = error.response?.data?.message;
          showSnackbar(message, SnackbarType.Error);
        }
      }
    },

    startTokenRefreshInterval: () => {
      const intervalId = setInterval(() => {
        getState().refreshToken();
      }, 600000);

      set({ intervalId });
    },

    stopTokenRefreshInterval: () => {
      const intervalId = getState().intervalId;
      if (intervalId) {
        clearInterval(intervalId);
      }
      set({ intervalId: null });
    },

    setCallState: (state) => {
      set({ callState: state });
    },
    setSelectedHotelId: (id: number | null) => {
      set({ selectedHotelId: id });
    },

    isSwitchHotelLoading: () =>
      getState().switchHotelCallState === CallStateEnum.Loading,
    isSwitchHotelLoaded: () =>
      getState().switchHotelCallState === CallStateEnum.Loaded,
    isSwitchHotelError: () =>
      getState().switchHotelCallState === CallStateEnum.Error
  }))
);

export default useAuthStore;
